import { useTranslation } from 'react-i18next';
import BoardTabs from '@/components/ui/BoardTabs/BoardTabs';
import styles from './SupportGoalsPage.module.scss';
import { useEffect, useMemo, useState } from 'react';
import GoalsSection from './sections/GoalsSection';
import useFundContext from '@/contexts/Fund/useFundContext';
import type { GoalDtoIn } from '@/models/in/GoalDtoIn';
import AllowedFor from '@/models/common/AllowedFor';
import GoalDetailsPanel from './GoalDetailsPanel/GoalDetailsPanel';
import GoalSupportProcedures from './GoalDetailsPanel/goalSupportProceduresView/GoalSupportProcedures';
import EmptyGoalDetails from './GoalDetailsPanel/emptyView/EmptyGoalDetails';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import arrowIcon from '@/assets/icons/arrowRight.svg';
import ProjectsEntryTile from './sections/ProjectsEntryTile';
import KollelsEntryTile from './sections/KollelsEntryTile';
import useFeature from '@/contexts/FeatureFlags/useFeature';

const separateGoals = (goals: GoalDtoIn[] = []) => {
  // Fund-expense goals include both FundExpenses (regular) and FundraisingExpenses
  // - the distinction between them is the limit, not the audience.
  const fundExpensesGoals = goals.filter((goal) => goal.allowedFor === AllowedFor.Fund);
  const supportedsGoals = goals.filter((goal) => goal.allowedFor !== AllowedFor.Fund);
  return { supportedsGoals, fundExpensesGoals };
};

const SupportGoalsPage = () => {
  const { currentFund } = useFundContext();
  const { t } = useTranslation();
  const [selectedGoal, setSelectedGoal] = useState<GoalDtoIn | null>(null);
  const { isMobile } = useDeviceDetection();
  const isProjectsEnabled = useFeature('projects');
  const isKollelsEnabled = useFeature('kollels');
  const { data: goals, isLoading: isGoalsLoading } = useGetAuthApi<GoalDtoIn[]>(currentFund ? `funds/${currentFund?.id}/goals` : '');
  const { supportedsGoals, fundExpensesGoals } = useMemo(
    () => separateGoals(goals ?? []),
    [goals],
  );

  useEffect(() => {
    setSelectedGoal(null);
  }, [currentFund?.id]);

  const showTiles = !isMobile || !selectedGoal;
  const showDetails = !isMobile || !!selectedGoal;

  return (
    <div className={styles.page}>
      {/* ניווט הלוחות במסך צר (HAT-1170). מחזיר null מעל 1100. */}
      <BoardTabs />
      {showTiles && (
        <div className={`${styles.content} custom-scrollbar`}>
          <GoalsSection
            title="goalsForSupporteds"
            goals={supportedsGoals ?? []}
            isLoading={isGoalsLoading}
            selectedGoalId={selectedGoal?.id}
            onSelectGoal={(goal) => setSelectedGoal(goal)}
            // "ניהול כוללים" groups existing supporteds, the same audience as this section, so it is
            // a navigation tile here rather than a goal (HAT-1446). It leads the list: placed last,
            // it sat below the fold right under the look-alike "כולל" goal, and the office opened
            // that goal instead and could not find where to set up a kollel.
            leadingTile={isKollelsEnabled ? <KollelsEntryTile /> : undefined}
          />
          <GoalsSection
            title="goalsForFundExpenses"
            goals={fundExpensesGoals ?? []}
            isLoading={isGoalsLoading}
            selectedGoalId={selectedGoal?.id}
            onSelectGoal={(goal) => setSelectedGoal(goal)}
            // "הוצאות פרויקט" belongs among the fund-expense goals, per the design; it is a
            // navigation tile rather than a goal, so it is not part of the goals list itself.
            extraTile={isProjectsEnabled ? <ProjectsEntryTile fundId={currentFund?.id} /> : undefined}
          />
        </div>
      )}
      {showDetails && (
        <>
          {isMobile && selectedGoal && (
            <button
              className={styles.backButton}
              onClick={() => setSelectedGoal(null)}
            >
              <img src={arrowIcon} alt="" className={styles.backArrow} />
              <span>{t('backToGoals')}</span>
            </button>
          )}
          <GoalDetailsPanel>
            {selectedGoal ? (
              <GoalSupportProcedures goal={selectedGoal} />
            ) : (
              <EmptyGoalDetails />
            )}
          </GoalDetailsPanel>
        </>
      )}
    </div>
  );
};

export default SupportGoalsPage;
